/* eslint-disable @typescript-eslint/no-explicit-any */
import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'

import CloseIcon from '../assets/icons/close.svg?react'
import SearchIcon from '../assets/icons/search.svg?react'
import { OptionWithData } from '../types'
import { Input } from './Input'

type AutoCompleteSearchProps<Data> = {
  options: OptionWithData<Data>[]
  placeholder?: string
  searchQuery?: string
  onSearchQueryChange?: (value: string) => void
  onSelectOption?: (option: OptionWithData<Data>) => void
  disabled?: boolean
}

export const AutocompleteSearch: React.FC<AutoCompleteSearchProps<any>> = <Data,>({
  searchQuery = '',
  onSearchQueryChange,
  placeholder,
  options,
  onSelectOption,
  disabled
}: AutoCompleteSearchProps<Data>) => {
  const [hasOptionsExpanded, setHasOptionsExpanded] = useState(false)

  const [cursorIndex, setCursorIndex] = useState(0)

  const handleClearSearchQuery = (e: React.MouseEvent) => {
    e.preventDefault()
    setHasOptionsExpanded(false)
    onSearchQueryChange?.('')
  }

  const handleSearchQueryChange = (value: string) => {
    onSearchQueryChange?.(value)
  }

  const element = useRef<HTMLDivElement>(null)

  const optionsListElement = useRef<HTMLDivElement>(null)

  const handleClickOutside = (e: MouseEvent) => {
    if (element.current && !element.current.contains(e.target as Node)) {
      setHasOptionsExpanded(false)
    }
  }

  const handleSelectOption = (option: OptionWithData<Data>) => {
    onSelectOption?.(option)
    setHasOptionsExpanded(false)
    onSearchQueryChange?.('')
  }

  const handleSearchFocus = () => {
    setHasOptionsExpanded(true)
    setCursorIndex(0)
  }

  const scrollToOption = (index: number) => {
    if (optionsListElement.current) {
      const option = optionsListElement.current.children?.[index]

      if (option) {
        option?.scrollIntoView({ block: 'nearest' })
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!hasOptionsExpanded) {
      setHasOptionsExpanded(true)
      setCursorIndex(0)
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      setHasOptionsExpanded(false)
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      moveCursor('up')
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      moveCursor('down')
    }

    if (e.key === 'Enter') {
      e.preventDefault()

      if (options[cursorIndex]) {
        handleSelectOption(options[cursorIndex])
      }
    }
  }

  const moveCursor = (direction: 'up' | 'down') => {
    if (direction === 'up') {
      if (cursorIndex > 0) {
        setCursorIndex(cursorIndex - 1)
      }
    } else {
      if (cursorIndex < options.length - 1) {
        setCursorIndex(cursorIndex + 1)
      }
    }
  }

  useEffect(() => {
    setHasOptionsExpanded(options.length > 0)
  }, [options])

  useEffect(() => {
    scrollToOption(cursorIndex)
  }, [cursorIndex])

  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutside)

    return () => {
      window.removeEventListener('mousedown', handleClickOutside)
    }
  })

  return (
    <div className="relative" ref={element}>
      <Input
        prefixIcon={SearchIcon}
        suffix={
          searchQuery ? (
            <button onClick={handleClearSearchQuery} className="size-8">
              <CloseIcon className="size-5" />
            </button>
          ) : undefined
        }
        value={searchQuery}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => handleSearchQueryChange(e.target.value)}
        className="w-full"
        onFocus={handleSearchFocus}
        onKeyDown={handleKeyDown}
      />

      {options.length > 0 && hasOptionsExpanded && (
        <div
          ref={optionsListElement}
          className="absolute top-full left-0 w-full bg-neutralBlack-200 rounded-lg mt-1 py-2 max-h-32 overscroll-y-contain overflow-y-auto shadow-lg z-10"
        >
          {options.map((option, index) => (
            <div key={option.value} className={classNames({ 'bg-black/5': index === cursorIndex })}>
              <button
                key={option.value}
                className="px-4 py-1 block truncate hover:bg-black/5 w-full text-left"
                onClick={() => handleSelectOption(option)}
              >
                {option.label}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
